
import { TrendingUp, TrendingDown, Wallet, Receipt, Landmark } from "lucide-react";
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import DashboardLayout from "../../components/DashboardLayout";
import { reportsApi } from "@/lib/api";
import { localToday, formatCurrencyShort } from "@/lib/data";
import {
  Card, ExportButtons, HeaderTools, Kpi, KpiGrid, NoMoneyAccess, Note, RangeControls, ReportBody, ReportHeader, TableScroll,
  money, pct, th, td, useCanSeeMoney, useRange, useReport, yearRange, type ExportSpec,
} from "../ReportKit";

interface Month {
  month: string; month_name: string;
  received: number; gst: number; income: number;
  vendor_payments: number; expenses: number; paid_out: number;
  profit: number; events: number; payments: number;
}
interface Trends {
  period: { from: string; to: string };
  monthly: Month[];
  summary: {
    received: number; gst: number; income: number; paid_out: number; profit: number; margin: number;
    events: number; avg_monthly_income: number;
    best_month: { month: string; month_name: string; income: number } | null;
  };
}

export default function RevenueTrendsPage() {
  const canSeeMoney = useCanSeeMoney();
  const range = useRange({ from: yearRange(0).from, to: localToday() });
  const { from, to } = range.applied;

  const state = useReport<Trends>(
    async () => (await reportsApi.revenueTrends({ from_date: from, to_date: to })).data,
    [from, to],
    canSeeMoney,
  );

  return (
    <DashboardLayout>
      <div className="space-y-5">
        <ReportHeader
          icon={TrendingUp}
          iconClass="text-orange-600"
          title="Revenue Trends"
          subtitle="Money received, paid out and profit, month by month"
        >
          {canSeeMoney && (
            <HeaderTools>
              <RangeControls range={range} />
              {state.status !== 403 && (
                <ExportButtons
                  build={() => state.data && buildExport(state.data, { from, to })}
                  disabled={state.loading || !state.data}
                />
              )}
            </HeaderTools>
          )}
        </ReportHeader>

        {!canSeeMoney ? <NoMoneyAccess /> : (
          <ReportBody state={state}>
            {(d) => <TrendsView d={d} />}
          </ReportBody>
        )}
      </div>
    </DashboardLayout>
  );
}

const NOTE =
  "Same figures as the Financial Summary for the same dates: money on the day it was received or paid out. " +
  "Events = events (not cancelled) starting that month.";

/** Excel / Print: the headline figures and the month-by-month table (with the chart's "received excl. GST"). */
function buildExport(d: Trends, period: { from: string; to: string }): ExportSpec {
  const s = d.summary;
  const isProfit = s.profit >= 0;
  return {
    title: "Revenue Trends",
    fileName: "revenue-trends",
    period,
    summary: [
      { label: "Received (incl. GST)", value: s.received, type: "money" },
      { label: "GST collected", value: s.gst, type: "money" },
      { label: "Paid out", value: s.paid_out, type: "money" },
      { label: isProfit ? "Profit" : "Loss", value: Math.abs(s.profit), type: "money" },
      { label: "Margin", value: s.margin, type: "percent" },
      ...(s.best_month ? [{ label: "Best month", value: s.best_month.month_name }] : []),
    ],
    tables: [
      {
        title: "Monthly breakdown",
        columns: [
          { label: "Month" },
          { label: "Received", type: "money" },
          { label: "GST", type: "money" },
          { label: "Received (excl. GST)", type: "money" },
          { label: "Paid out", type: "money" },
          { label: "Profit", type: "money" },
          { label: "Events", type: "number" },
        ],
        rows: d.monthly.map((m) => [m.month_name, m.received, m.gst, m.income, m.paid_out, m.profit, m.events]),
        totals: d.monthly.length ? ["Total", s.received, s.gst, s.income, s.paid_out, s.profit, s.events] : undefined,
      },
    ],
    notes: [`Profit = received − GST − paid out. ${NOTE}`],
  };
}

function TrendsView({ d }: { d: Trends }) {
  const s = d.summary;
  const isProfit = s.profit >= 0;
  const chart = d.monthly.map((m) => ({
    month: m.month_name,
    income: m.income,
    paid_out: m.paid_out,
    profit: m.profit,
  }));
  const labels: Record<string, string> = { income: "Received (excl. GST)", paid_out: "Paid out", profit: "Profit" };

  return (
    <>
      <KpiGrid>
        <Kpi label="Received" icon={Wallet} tone="emerald" value={money(s.received)} sub={`incl. ${money(s.gst)} GST`} />
        <Kpi label="GST collected" icon={Landmark} tone="violet" value={money(s.gst)} sub="Owed to the government, not income" />
        <Kpi label="Paid out" icon={Receipt} tone="amber" value={money(s.paid_out)} sub="Vendor payments + expenses" />
        <Kpi
          label={isProfit ? "Profit" : "Loss"} icon={isProfit ? TrendingUp : TrendingDown} tone={isProfit ? "blue" : "red"}
          value={money(Math.abs(s.profit))}
          valueClass={isProfit ? "text-blue-800" : "text-red-700"}
          sub={`${pct(s.margin)} margin${s.best_month ? ` · best: ${s.best_month.month_name}` : ""}`}
        />
      </KpiGrid>

      <Card title="Month by month">
        {chart.length === 0 ? (
          <p className="text-sm text-stone-400 py-10 text-center">No data</p>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={chart} barCategoryGap="25%">
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f4" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => formatCurrencyShort(Number(v))} width={56} />
              <Tooltip formatter={(v: any, name: any) => [money(v), labels[name] ?? name]} />
              <Legend formatter={(v) => labels[v] ?? v} wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="income" fill="#10b981" radius={[4, 4, 0, 0]} />
              <Bar dataKey="paid_out" fill="#f59e0b" radius={[4, 4, 0, 0]} />
              <Line type="monotone" dataKey="profit" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </Card>

      <Card title="Monthly breakdown" subtitle="Profit = received − GST − paid out">
        <TableScroll minWidth={620}>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-stone-100">
                <th className={`${th} text-left`}>Month</th>
                <th className={`${th} text-right`}>Received</th>
                <th className={`${th} text-right`}>GST</th>
                <th className={`${th} text-right`}>Paid out</th>
                <th className={`${th} text-right`}>Profit</th>
                <th className={`${th} text-right`}>Events</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-50">
              {d.monthly.map((m) => (
                <tr key={m.month} className={s.best_month?.month === m.month ? "bg-emerald-50/60" : ""}>
                  <td className={`${td} font-medium text-stone-700`}>
                    {m.month_name}
                    {s.best_month?.month === m.month && <span className="ml-2 text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">Best</span>}
                  </td>
                  <td className={`${td} text-right tabular-nums text-stone-800`}>{money(m.received)}</td>
                  <td className={`${td} text-right tabular-nums text-stone-500`}>{money(m.gst)}</td>
                  <td className={`${td} text-right tabular-nums text-stone-600`}>{money(m.paid_out)}</td>
                  <td className={`${td} text-right tabular-nums font-semibold ${m.profit >= 0 ? "text-emerald-700" : "text-red-600"}`}>{money(m.profit)}</td>
                  <td className={`${td} text-right text-stone-600`}>{m.events}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-stone-200 bg-stone-50 font-bold">
                <td className={`${td} text-stone-800`}>Total</td>
                <td className={`${td} text-right tabular-nums`}>{money(s.received)}</td>
                <td className={`${td} text-right tabular-nums text-stone-500`}>{money(s.gst)}</td>
                <td className={`${td} text-right tabular-nums`}>{money(s.paid_out)}</td>
                <td className={`${td} text-right tabular-nums ${isProfit ? "text-emerald-700" : "text-red-600"}`}>{money(s.profit)}</td>
                <td className={`${td} text-right`}>{s.events}</td>
              </tr>
            </tfoot>
          </table>
        </TableScroll>
      </Card>

      <Note>{NOTE}</Note>
    </>
  );
}
